import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { authClient, userManager } from "../lib/auth";

// Module-level guards — survive React StrictMode unmount/remount.
// One-time codes/tokens can only be exchanged once; a duplicate returns 400.
const processedTokens = new Set<string>();
const processedCodes = new Set<string>();

type Flow360ExchangePayload = {
  auth?: {
    accessToken?: string;
    access_token?: string;
    AccessToken?: string;
    refreshToken?: string;
    refresh_token?: string;
    RefreshToken?: string;
    idToken?: string;
    id_token?: string;
    IdToken?: string;
  } | null;
  data?: {
    auth?: {
      accessToken?: string;
      access_token?: string;
      AccessToken?: string;
      refreshToken?: string;
      refresh_token?: string;
      RefreshToken?: string;
      idToken?: string;
      id_token?: string;
      IdToken?: string;
    } | null;
    user?: { email?: string; emailAddress?: string } | null;
  } | null;
  user?: { email?: string; emailAddress?: string } | null;
  cognito_sub?: string | null;
  thread_session?: { access_token?: string; refresh_token?: string } | null;
  flow360_api_key_stored?: boolean;
};

type PostAuthDestination = {
  path: string;
  reload: boolean;
};

/**
 * Auth callback page — handles redirects from:
 * 1. Keycloak OIDC (Google, GitHub federation) — exchanges ?code= callbacks.
 * 2. Flow360 Login (SimCloud portal) — we receive a one-time token in the
 *    `_token` query param that needs to be exchanged via our backend.
 * 3. Flow360 Login (local dev cookie flow) — local Flow360 UI sets cookies
 *    on .dev-simulation.cloud instead of passing _token.
 */
export default function AuthCallback() {
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  /**
   * Recognised return-path segments for the CLI/desktop auth flows. The
   * stored ``cli_auth_return`` value MUST contain one of these — anything
   * else is treated as malformed and we fall through to ``/`` rather than
   * blindly redirecting the browser somewhere unexpected.
   */
  const CLI_RETURN_SEGMENTS = ["/connect/bobbin", "/auth/cli"];
  const routerBasename = import.meta.env.BASE_URL.replace(/\/$/, "");

  function relativeUrl(path: string): URL | null {
    try {
      const url = new URL(path, window.location.origin);
      return path.startsWith("/") && url.origin === window.location.origin
        ? url
        : null;
    } catch {
      return null;
    }
  }

  function stripBasename(pathname: string): string {
    if (!routerBasename) return pathname;
    if (pathname === routerBasename) return "/";
    if (pathname.startsWith(`${routerBasename}/`)) {
      return pathname.slice(routerBasename.length);
    }
    return pathname;
  }

  function pathFromUrl(url: URL, stripBase: boolean): string {
    const pathname = stripBase ? stripBasename(url.pathname) : url.pathname;
    return `${pathname}${url.search}${url.hash}`;
  }

  function isBlockedPostAuthDestination(url: URL): boolean {
    const pathname = stripBasename(url.pathname);
    return pathname === "/sign-in" || pathname.startsWith("/auth/callback");
  }

  function isCliReturn(url: URL): boolean {
    const hasRouterBasename =
      !routerBasename ||
      url.pathname === routerBasename ||
      url.pathname.startsWith(`${routerBasename}/`);
    return (
      hasRouterBasename &&
      CLI_RETURN_SEGMENTS.includes(stripBasename(url.pathname))
    );
  }

  function getPostAuthDestination(): PostAuthDestination {
    const next = searchParams.get("next");
    if (next) {
      // The OAuth cross-origin handoff (App.tsx) puts the original
      // pre-sign-in path here, including the `/thread` basename for
      // CLI/Bobbin returns. Force a full reload for those so we bypass
      // React Router's basename handling — same trick we use for
      // `cli_auth_return` below. Without this, navigate() either drops
      // or doubles the basename and the user lands on the home page.
      const nextUrl = relativeUrl(next);
      if (nextUrl) {
        if (isBlockedPostAuthDestination(nextUrl)) {
          console.warn(
            "[AuthCallback] ignoring next that would loop:",
            next,
          );
          return { path: "/", reload: false };
        }
        const reload = isCliReturn(nextUrl);
        return { path: pathFromUrl(nextUrl, !reload), reload };
      }
      console.warn(
        "[AuthCallback] next is not a same-origin path; ignoring:",
        next,
      );
    }
    const stored = sessionStorage.getItem("cli_auth_return");
    if (stored) {
      sessionStorage.removeItem("cli_auth_return");
      // Loud failure: the CLI/Bobbin flows store a known path shape. If
      // something else ends up in this slot, log it and fall back to / so
      // we don't bounce the user to a router-internal URL that React
      // Router will reject (the basename-stripping bug we are fixing).
      const storedUrl = relativeUrl(stored);
      if (!storedUrl || !isCliReturn(storedUrl)) {
        console.warn(
          "[AuthCallback] cli_auth_return is set but malformed; ignoring:",
          stored,
        );
      } else {
        return { path: pathFromUrl(storedUrl, false), reload: true };
      }
    }
    const postAuth = sessionStorage.getItem("thread.post_auth_redirect");
    if (postAuth) {
      sessionStorage.removeItem("thread.post_auth_redirect");
      // Defensive: never bounce the user back to /sign-in or
      // /auth/callback. The authed routes block has no /sign-in route
      // (would render blank), and /auth/callback re-enters this flow.
      const postAuthUrl = relativeUrl(postAuth);
      if (postAuthUrl && isBlockedPostAuthDestination(postAuthUrl)) {
        console.warn(
          "[AuthCallback] ignoring post_auth_redirect that would loop:",
          postAuth,
        );
      } else {
        return { path: postAuth, reload: false };
      }
    }
    return { path: "/", reload: false };
  }

  /**
   * Navigate to *destination* after sign-in. The CLI and Bobbin flows
   * store an absolute pathname (e.g. ``/thread/connect/bobbin?…``) so we
   * can call ``window.location.assign`` and re-enter the app with a fresh
   * page load — that bypasses React Router's basename handling, which
   * dropped the ``/thread`` prefix on the in-process ``navigate(...)``
   * round-trip and rendered a blank page. Router-internal paths still go
   * through ``navigate`` after basename normalization.
   */
  function goToDestination(destination: PostAuthDestination) {
    if (destination.reload) {
      window.location.assign(destination.path);
      return;
    }
    navigate(destination.path, { replace: true });
  }

  function finishFlow360Login(destination: PostAuthDestination) {
    // sessionStorage storage events do not fire in the same tab. This custom
    // event lets App.tsx update its Flow360-only auth state immediately.
    window.dispatchEvent(new CustomEvent("flow360-auth-ready"));
    goToDestination(destination);
  }

  function storeFlow360Payload(data: Flow360ExchangePayload): boolean {
    const auth = data.auth ?? data.data?.auth ?? null;
    const accessToken = auth?.accessToken ?? auth?.access_token ?? auth?.AccessToken;
    const refreshToken = auth?.refreshToken ?? auth?.refresh_token ?? auth?.RefreshToken;
    const userEmail =
      data.user?.email ??
      data.user?.emailAddress ??
      data.data?.user?.email ??
      data.data?.user?.emailAddress;

    if (accessToken) {
      sessionStorage.setItem("flow360_access_token", accessToken);
    }
    if (refreshToken) {
      sessionStorage.setItem("flow360_refresh_token", refreshToken);
    }
    if (data.cognito_sub) {
      sessionStorage.setItem("flow360_cognito_sub", data.cognito_sub);
    }
    if (userEmail) {
      sessionStorage.setItem("flow360_user_email", userEmail);
    }
    if (data.flow360_api_key_stored) {
      sessionStorage.setItem("flow360_api_key_stored", "true");
    } else if (data.flow360_api_key_stored === false) {
      sessionStorage.setItem("flow360_api_key_stored", "false");
    }
    return !!accessToken;
  }

  useEffect(() => {
    const provider = searchParams.get("provider");
    const oneTimeToken = searchParams.get("_token");
    const emailMagicToken = searchParams.get("token");
    const destination = getPostAuthDestination();

    // Skip if we've already started processing this exact token
    if (oneTimeToken && processedTokens.has(oneTimeToken)) return;
    if (oneTimeToken) processedTokens.add(oneTimeToken);

    if (provider === "email" && emailMagicToken) {
      const dedupeKey = `email:${emailMagicToken}`;
      if (processedTokens.has(dedupeKey)) return;
      processedTokens.add(dedupeKey);
      handleEmailMagicLink(emailMagicToken, destination);
    } else if (provider === "flow360" && oneTimeToken) {
      handleFlow360Token(oneTimeToken, destination);
    } else if (provider === "flow360") {
      handleFlow360Cookies(destination);
    } else if (searchParams.get("error")) {
      // Keycloak returned an error (e.g. cookie_not_found during GitHub IDP flow).
      const errCode = searchParams.get("error") ?? "unknown";
      const errDesc = searchParams.get("error_description") ?? "";
      console.error("Auth callback error:", errCode, errDesc);
      setError(`Sign in failed (${errCode}). Please try again.`);
    } else if (searchParams.get("code")) {
      // OIDC authorization code callback from Keycloak (Google / GitHub via Keycloak federation).
      // The authorization code in ?code= must be exchanged for tokens by oidc-client-ts.
      const code = searchParams.get("code")!;
      if (processedCodes.has(code)) return;
      processedCodes.add(code);

      userManager
        .signinRedirectCallback()
        .then(() => {
          goToDestination(destination);
        })
        .catch((err: unknown) => {
          console.error("OIDC callback failed:", err);
          setError("Sign in failed: " + (err instanceof Error ? err.message : String(err)));
        });
    } else {
      // Already authenticated — check for an existing session and navigate.
      authClient.auth.getSession().then(({ data: { session } }) => {
        if (session) {
          goToDestination(destination);
          return;
        }
        // Listen for session to be established (e.g. silent renew or concurrent tab).
        const { data: { subscription } } = authClient.auth.onAuthStateChange(
          (_event, session) => {
            if (session) {
              subscription.unsubscribe();
              goToDestination(destination);
            }
          }
        );
        // Safety timeout — show error if no session arrives within 15 seconds.
        setTimeout(() => {
          subscription.unsubscribe();
          authClient.auth.getSession().then(({ data: { session: s } }) => {
            if (s) {
              goToDestination(destination);
            } else {
              setError("Authentication failed. Please try again.");
            }
          });
        }, 15000);
      });
    }
  }, [searchParams, navigate]);

  async function handleEmailMagicLink(token: string, destination: PostAuthDestination) {
    const { error: emailError } = await authClient.auth.verifyEmailMagicLink(token);
    if (emailError) {
      setError(emailError.message);
      return;
    }
    goToDestination(destination);
  }

  /**
   * Local dev flow: the local Flow360 UI sets cookies on .dev-simulation.cloud.
   * Read them, store in sessionStorage, and mint a Thread session via backend
   * when Keycloak is available.
   */
  async function handleFlow360Cookies(destination: PostAuthDestination) {
    function getCookie(name: string): string | null {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop()?.split(";").shift() || null;
      return null;
    }

    const accessToken = getCookie("FLOW360_ACCESS_TOKEN");
    if (!accessToken) {
      setError("Flow360 login succeeded but no tokens found. Check that you're on thread-local.simulation.cloud.");
      return;
    }

    sessionStorage.setItem("flow360_access_token", accessToken);
    const refreshToken = getCookie("FLOW360_REFRESH_TOKEN");
    if (refreshToken) sessionStorage.setItem("flow360_refresh_token", refreshToken);

    const cookieUser = getCookie("FLOW360_COOKIE_USER");
    if (cookieUser) {
      try {
        const user = JSON.parse(decodeURIComponent(cookieUser));
        if (user.email) sessionStorage.setItem("flow360_user_email", user.email);
      } catch { /* ignore parse errors */ }
    }

    // Mint a Keycloak session from the Flow360 token so that thread-api routes
    // requiring Authorization: Bearer <keycloak_jwt> work without re-login.
    try {
      const API_BASE = import.meta.env.VITE_API_URL ?? "/api";
      const mintRes = await fetch(`${API_BASE}/flow360/auth/mint-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ flow360_access_token: accessToken }),
      });

      if (mintRes.ok) {
        const mintData = await mintRes.json();
        const threadSession = mintData.thread_session;
        if (threadSession?.access_token && threadSession?.refresh_token) {
          const { error: sessionError } = await authClient.auth.setSession({
            access_token: threadSession.access_token,
            refresh_token: threadSession.refresh_token,
          });
          if (sessionError) {
            console.warn("[cookie-flow] Failed to set Keycloak session:", sessionError);
          }
        }
        if (mintData.user?.email) {
          sessionStorage.setItem("flow360_user_email", mintData.user.email);
        }
        if (mintData.flow360_api_key_stored) {
          sessionStorage.setItem("flow360_api_key_stored", "true");
        } else if (mintData.flow360_api_key_stored === false) {
          sessionStorage.setItem("flow360_api_key_stored", "false");
        }
      } else {
        console.warn("[cookie-flow] mint-session failed:", mintRes.status);
      }
    } catch (err) {
      // Non-critical — cookie flow still works without Keycloak JWT for browsing-only
      console.warn("[cookie-flow] mint-session error:", err);
    }

    // Notify App.tsx that flow360 auth is ready (sessionStorage storage events
    // don't fire in the same tab, so we use a custom event instead).
    window.dispatchEvent(new CustomEvent("flow360-auth-ready"));
    finishFlow360Login(destination);
  }

  async function handleFlow360Token(oneTimeToken: string, destination: PostAuthDestination) {
    try {
      // Exchange one-time token via our backend (avoids CORS)
      const API_BASE = import.meta.env.VITE_API_URL ?? "/api";
      const res = await fetch(
        `${API_BASE}/flow360/auth/token/exchange?oneTimeToken=${encodeURIComponent(oneTimeToken)}`,
      );

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Token exchange failed: ${text}`);
      }

      const data: Flow360ExchangePayload = await res.json();

      // Store Flow360 tokens for Flow360 API access. The portal/backend have
      // used both camelCase and snake_case token keys over time, so normalize.
      const storedFlow360Token = storeFlow360Payload(data);

      // Establish an auth session from the server-minted Keycloak tokens.
      // The backend mints a user-scoped Keycloak token via admin API + token exchange (RFC 8693).
      const threadSession = data.thread_session;
      if (threadSession?.access_token && threadSession?.refresh_token) {
        const { error: sessionError } = await authClient.auth.setSession({
          access_token: threadSession.access_token,
          refresh_token: threadSession.refresh_token,
        });

        if (sessionError) {
          console.error("Failed to set auth session:", sessionError);
          setError(`Auth session error: ${sessionError.message}`);
          return;
        }

        goToDestination(destination);
        return;
      }

      // Fallback: no auth session minted (backend missing KEYCLOAK_URL config)
      const email =
        data.user?.email ??
        data.user?.emailAddress ??
        data.data?.user?.email ??
        data.data?.user?.emailAddress;
      if (email && storedFlow360Token) {
        console.info("Flow360 login succeeded in Flow360-only mode; Keycloak session minting is not configured locally.");
        finishFlow360Login(destination);
      } else if (email) {
        setError("Flow360 login returned a user but no access token. Please try signing in again.");
      } else {
        setError("No email returned from Flow360. Please try another login method.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Flow360 login failed");
    }
  }

  if (error) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          gap: "16px",
        }}
      >
        <p style={{ color: "var(--red-9)" }}>{error}</p>
        <button
          onClick={() => navigate("/", { replace: true })}
          style={{
            padding: "8px 16px",
            borderRadius: "8px",
            border: "1px solid var(--gray-7)",
            background: "var(--gray-3)",
            color: "var(--gray-12)",
            cursor: "pointer",
          }}
        >
          Back to Sign In
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
      }}
    >
      <p style={{ color: "var(--gray-9)" }}>Completing sign in...</p>
    </div>
  );
}
